import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { takeUntil } from 'rxjs';
import { DestroyService } from '@selfai-platform/shared';
import { ChatService, MonacoConfigService } from '../../services';

@Component({
  selector: 'selfai-platform-chat-context-viewer',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, MonacoEditorModule, ButtonModule, TagModule, TooltipModule],
  templateUrl: './chat-context-viewer.component.html',
  styleUrls: ['./chat-context-viewer.component.scss'],
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatContextViewerComponent implements OnInit {
  public contextData = '';
  public formattedContext = '';
  public editorOptions: any = {};
  public isEmpty = false;
  public lastUpdated: Date | null = null;
  private parsedContext: any = null;

  constructor(
    private readonly chatService: ChatService,
    private readonly dialogRef: DynamicDialogRef,
    private readonly config: DynamicDialogConfig,
    private readonly destroy$: DestroyService,
    private readonly cdr: ChangeDetectorRef,
    private readonly monacoConfigService: MonacoConfigService
  ) {}

  ngOnInit(): void {
    this.setupEditorOptions();
    this.subscribeToContext();
  }

  private setupEditorOptions(): void {
    this.editorOptions = this.monacoConfigService.getEditorConfig(
      'json',
      'context'
    );
  }

  private subscribeToContext(): void {
    this.chatService.contextSubject.pipe(
      takeUntil(this.destroy$)
    ).subscribe((context) => {
      this.contextData = context;
      this.formatContext();
      this.lastUpdated = context ? new Date() : null;
      this.cdr.markForCheck();
    });
  }

  private formatContext(): void {
    if (!this.contextData || this.contextData.trim() === '') {
      this.formattedContext = '';
      this.isEmpty = true;
      return;
    }

    try {
      this.parsedContext = JSON.parse(this.contextData);

      if (this.parsedContext.schema && Array.isArray(this.parsedContext.schema)) {
        this.parsedContext.schema = this.parsedContext.schema.map((field: any) => ({
          name: field.name,
          type: field.type,
          nullable: field.nullable,
          metadata: field.metadata
        }));
      }

      this.formattedContext = JSON.stringify(this.parsedContext, null, 2);
      this.isEmpty = false;
    } catch (error) {
      this.parsedContext = null;
      this.formattedContext = this.contextData;
      this.isEmpty = false;
    }
  }

  public clearContext(): void {
    this.chatService.setContext('').subscribe(() => {
      this.contextData = '';
      this.formattedContext = '';
      this.isEmpty = true;
      this.lastUpdated = null;
      this.cdr.markForCheck();
    });
  }

  public copyToClipboard(): void {
    if (!this.formattedContext) return;

    if (navigator.clipboard) {
      navigator.clipboard.writeText(this.formattedContext);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = this.formattedContext;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  public getContextSize(): string {
    if (!this.formattedContext) return '0 characters';

    const size = this.formattedContext.length;
    if (size < 1024) {
      return `${size} characters`;
    } else if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    } else {
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }
  }

  public hasSchema(): boolean {
    return (this.parsedContext?.schema && Array.isArray(this.parsedContext.schema) && this.parsedContext.schema.length > 0);
  }

  public getSchemaFields(): any[] {
    if (!this.hasSchema()) return [];
    return this.parsedContext.schema;
  }

  public getSchemaCount(): number {
    return this.getSchemaFields().length;
  }
}
